import { tv, type VariantProps } from "@ui/lib/tv";
import type { AccentColor } from "@ui/types/accent";

/** Resting pill chrome: a hairline ring plus a 1px bottom edge, both as box-shadow so the
 *  focus/validation halos can cross-fade over the same geometry. */
export const wizardFieldRestingShadow = "shadow-[0_0_0_1px_rgba(0,0,0,0.12),0_1px_0_0_rgba(0,0,0,0.16)]";

/** Disabled fields drop the bottom edge and fade the hairline. */
export const wizardFieldDisabledShadow = "shadow-[0_0_0_1px_rgba(0,0,0,0.08)]";

/** Focus halo per accent colour — an 8px soft outer ring over a 2px hard inner ring. Validation
 *  rings reuse the exact geometry (only the colour differs) so the ring never changes size when a
 *  field transitions focus → valid/invalid mid-type — a width change there reads as jitter.
 *  Mirrors Field.Control, which shares geometry across states. */
export const wizardFieldFocusRing = {
  red: "shadow-[0_0_0_8px_var(--color-red-300),0_0_0_2px_var(--color-red-400)]",
  green: "shadow-[0_0_0_8px_var(--color-green-200),0_0_0_2px_var(--color-green-500)]",
  purple: "shadow-[0_0_0_8px_var(--color-purple-300),0_0_0_2px_var(--color-purple-500)]",
  yellow: "shadow-[0_0_0_8px_var(--color-yellow-200),0_0_0_2px_var(--color-yellow-500)]",
} satisfies Record<AccentColor, string>;

/** Same focus halos as CSS `focus-within:` rules, for fields whose primitive exposes no
 *  focus-state render prop (react-aria's DateField, unlike TextFieldGroup). Tailwind needs these
 *  spelled out — it cannot see a runtime-prefixed `focus-within:${...}`. */
const wizardFieldFocusWithinRing = {
  red: "focus-within:shadow-[0_0_0_8px_var(--color-red-300),0_0_0_2px_var(--color-red-400)]",
  green: "focus-within:shadow-[0_0_0_8px_var(--color-green-200),0_0_0_2px_var(--color-green-500)]",
  purple: "focus-within:shadow-[0_0_0_8px_var(--color-purple-300),0_0_0_2px_var(--color-purple-500)]",
  yellow: "focus-within:shadow-[0_0_0_8px_var(--color-yellow-200),0_0_0_2px_var(--color-yellow-500)]",
} satisfies Record<AccentColor, string>;

/** Pink (not red) invalid halo, reading as one colour with the icon + message — matches Field. */
export const wizardFieldInvalidRing = "shadow-[0_0_0_8px_var(--color-pink-200),0_0_0_2px_var(--color-pink-500)]";

/** Lighter halo than the focus ring — a calm "done", not a celebration, for merely typing. */
export const wizardFieldSuccessRing = "shadow-[0_0_0_8px_var(--color-green-100),0_0_0_2px_var(--color-green-500)]";

/** Footer row under the field: validation message (left) ↔ character count (right). Reserved
 *  height so the centred stage doesn't shift when the message toggles; pt clears the outset halo;
 *  px (set per consumer/size) aligns it under the input text. */
export const wizardFieldMessageSlotClasses = "flex w-full items-start gap-3 min-h-8 pt-1.5";

export const wizardFieldMessageClasses = [
  "min-w-0 text-base/6",
  "motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-top-1 motion-safe:duration-200",
];

/** Quiet field metadata, pinned right; tabular-nums so the digits don't wobble while typing. */
export const wizardFieldCountClasses = "ml-auto shrink-0 text-sm/6 text-secondary tabular-nums";

/**
 * Shared chrome for the wizard field family (WizardTextField, MoneyField, WizardDateField).
 * WizardTextArea and WizardRichTextArea have structurally different boxes (the textarea/editor IS
 * the pill) and build their own tv from the exported ring/footer constants instead.
 */
export const wizardField = tv({
  slots: {
    root: "group flex w-full flex-col items-start justify-start gap-1.5",
    field: [
      "flex w-full items-center",
      "bg-white rounded-3xl",
      wizardFieldRestingShadow,
      // Halo (box-shadow) keeps a constant ring geometry, so this only ever cross-fades its colour —
      // green focus → pink invalid. 100ms with ease-out (not the original linear) keeps the change
      // snappy while softening the arrival so it doesn't snap.
      "transition duration-100 ease-out",
    ],
    leadIcon: "inline-flex shrink-0 items-center justify-center text-neutral-500",
    // Inline prefix rendered between the lead icon and the input (e.g. MoneyField's currency
    // symbol) — input-toned but muted, and never shrinks under the input's flex-1.
    prefix: "flex-none select-none font-normal text-neutral-500",
    input: [
      "min-w-0 flex-1 bg-transparent caret-current",
      "font-normal text-neutral-800 placeholder:text-neutral-500",
      "outline-hidden",
    ],
    validationIcon: "inline-flex shrink-0 items-center justify-center [&_svg]:size-5",
    messageSlot: wizardFieldMessageSlotClasses,
    message: wizardFieldMessageClasses,
    count: wizardFieldCountClasses,
  },
  variants: {
    color: {
      red: {},
      green: {},
      purple: {},
      yellow: {},
    },
    size: {
      xl: {
        field: "min-h-12 gap-2 px-4",
        input: "text-lg leading-6",
        prefix: "text-lg leading-6",
        leadIcon: "[&_svg]:size-6",
        messageSlot: "px-4",
      },
      "3xl": {
        field: "min-h-14 gap-3 px-4",
        input: "text-xl leading-6",
        prefix: "text-xl leading-6",
        leadIcon: "[&_svg]:size-7",
        messageSlot: "px-4",
      },
      "4xl": {
        field: "min-h-[60px] gap-3 px-5",
        input: "text-xl leading-6",
        prefix: "text-xl leading-6",
        leadIcon: "[&_svg]:size-7",
        messageSlot: "px-5",
      },
      "5xl": {
        // Steps down to 3xl metrics below `lg` so the field tracks the wizard's
        // bottom-bar button down-step (64px → 56px). Slot is over-sized vs the visible
        // glyph because the filled-icon SVG has internal whitespace + white stroke
        // around the silhouette — a 28px slot renders the coloured glyph at ~16px.
        field: "min-h-14 gap-3 px-4 lg:min-h-16 lg:px-5",
        input: "text-xl leading-6",
        prefix: "text-xl leading-6",
        leadIcon: "[&_svg]:size-7",
        messageSlot: "px-4 lg:px-5",
      },
    },
    // Render-prop-driven focus (TextFieldGroup's isFocusWithin). Callers pass
    // `isFocused && !validationState` so a validation ring keeps owning the shadow.
    isFocused: { true: {} },
    // CSS-driven focus for primitives without a focus render prop (DateField). Callers pass
    // `!validationState` so the validation ring keeps owning the shadow — with no focus-within
    // rule emitted, the resting validation halo simply persists through focus.
    focusWithin: { true: {} },
    isDisabled: { true: {} },
    validationState: {
      invalid: {
        field: wizardFieldInvalidRing,
        validationIcon: "text-pink-500",
        message: "text-pink-600",
      },
      success: {
        field: wizardFieldSuccessRing,
        validationIcon: "text-green-500",
        message: "text-green-600",
      },
    },
    // Character count turns pink only when the field is over a hard limit (invalid) or nearing a
    // `maxLength` (e.g. the tagline stage). The soft `recommendedLength` nudge stays muted — it's
    // guidance, not an error — matching Field.Description's secondary helper text.
    nearLimit: { true: { count: "text-pink-600" } },
  },
  compoundVariants: [
    {
      isDisabled: true,
      class: {
        field: ["bg-neutral-25", wizardFieldDisabledShadow],
        input: "cursor-not-allowed text-neutral-500",
      },
    },
    // Focus ring per colour (render-prop driven)
    { color: "red", isFocused: true, isDisabled: false, class: { field: wizardFieldFocusRing.red } },
    { color: "green", isFocused: true, isDisabled: false, class: { field: wizardFieldFocusRing.green } },
    { color: "purple", isFocused: true, isDisabled: false, class: { field: wizardFieldFocusRing.purple } },
    { color: "yellow", isFocused: true, isDisabled: false, class: { field: wizardFieldFocusRing.yellow } },
    // Focus ring per colour (focus-within driven)
    { color: "red", focusWithin: true, isDisabled: false, class: { field: wizardFieldFocusWithinRing.red } },
    { color: "green", focusWithin: true, isDisabled: false, class: { field: wizardFieldFocusWithinRing.green } },
    { color: "purple", focusWithin: true, isDisabled: false, class: { field: wizardFieldFocusWithinRing.purple } },
    { color: "yellow", focusWithin: true, isDisabled: false, class: { field: wizardFieldFocusWithinRing.yellow } },
  ],
  defaultVariants: {
    color: "purple",
    size: "5xl",
    isFocused: false,
    isDisabled: false,
  },
});

export type WizardFieldVariants = VariantProps<typeof wizardField>;
